"use client";

import { useParams, useRouter } from "next/navigation";
import { type Key, useCallback, useMemo } from "react";
import { IcSphere } from "@jetblue/kit-icons/ic-sphere";
import { InputSelect } from "@jetblue/kit/input-select";

import type { BrazeSDK } from "../../../types/braze";
import { LANGUAGE_INTEREST_EVENT } from "../../../../constants/braze/events";

type LanguageSelectorLabels = {
  en: string;
  es: string;
  fr: string;
  it?: string | undefined;
  language: string;
};

type LanguageSelectorClientProperties = {
  labels: LanguageSelectorLabels;
  languages: string[];
};

type PageParameters = {
  pathname?: string[];
  personalizations: string;
  language: string;
};

export const getOptions = (
  labels: LanguageSelectorLabels,
  languages: string[],
) => {
  const result: { label: string; value: string }[] = [];

  for (const language of languages) {
    const label = labels[language as keyof LanguageSelectorLabels];

    if (label) {
      result.push({ label, value: language });
    }
  }

  return result;
};
export const handleChangeLanguage = ({
  languages,
  logBrazeChangeLanguageEvent,
  pageParameters,
  router,
  value,
}: {
  languages: string[];
  logBrazeChangeLanguageEvent: ({
    selectedLanguage,
  }: {
    selectedLanguage: string | undefined;
  }) => void;
  pageParameters: PageParameters;
  router: ReturnType<typeof useRouter>;
  value: Key | null;
}) => {
  if (
    value &&
    value !== pageParameters.language &&
    languages.includes(value.toString())
  ) {
    /**
     * TECH DEBT: We plan to set this cookie with our library once it handles language persistence.
     */
    document.cookie = `lang=${value.toString()}; Path=/; ${process.env.NODE_ENV === "development" ? "" : "Domain=.jetblue.com;"}`;
    document.documentElement.lang = value.toString();
    logBrazeChangeLanguageEvent({ selectedLanguage: value.toString() });

    const pathname = pageParameters.pathname
      ? `/${pageParameters.pathname.join("/")}`
      : "/";

    const redirectPathname =
      (value === "en" ? "" : `/${value.toString()}`) + pathname;

    router.push(redirectPathname);
  }
};
export const LanguageSelectorClient = ({
  labels,
  languages,
}: LanguageSelectorClientProperties) => {
  const pageParameters = useParams<PageParameters>();
  const router = useRouter();
  const options = useMemo(
    () => getOptions(labels, languages),
    [labels, languages],
  );

  const logBrazeChangeLanguageEvent = useCallback(
    ({ selectedLanguage }: { selectedLanguage: string | undefined }) => {
      const braze = (globalThis as typeof globalThis & { braze?: BrazeSDK })
        .braze;

      if (selectedLanguage && braze?.logCustomEvent) {
        braze.logCustomEvent(LANGUAGE_INTEREST_EVENT, {
          previousLanguage: pageParameters.language,
          selectedLanguage,
        });
      }
    },
    [pageParameters.language],
  );

  return (
    <InputSelect
      Icon={IcSphere}
      className="
        text-s-body-extra-bold

        [&_button]:cursor-pointer
      "
      data-fs-element="footer-language-selector"
      iconClassName="size-4 stroke-royal-blue"
      label={labels.language}
      onChange={(value: Key | null) => {
        handleChangeLanguage({
          languages,
          logBrazeChangeLanguageEvent,
          pageParameters,
          router,
          value,
        });
      }}
      options={options}
      value={pageParameters.language}
      variant="simple"
    />
  );
};
